import store from '@/store';
const THREE = window.THREE;
import { toPoint2D, isLeft } from './threejsUtils';
import { ForgeAppearanceOptions, ForgeObject } from './../scripts/js/forgeModels';
import { takeScreenshot, ForgeVisiblityMode } from '../scripts/js/forgeService';
import HttpService from './httpService';
import Vue from 'vue';
import { getRegionString } from '@/utils/regionUtils';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { assertPersistableUnitScale, viewerToGlobal } from '@/utils/coordinateAuthority';
import { $hexToVector4 } from '@/utils';
import axios from 'axios';

import dbId2box from '@/extensions/dbId2box';

const Autodesk = window.Autodesk;

//https://adndevblog.typepad.com/cloud_and_mobile/2015/08/getting-bounding-boxes-of-each-component-in-the-viewer.html
export function getModifiedWorldBoundingBox(fragIds, model) {
  if (!model) model = store.state.viewer.model;

  //fragments list array
  var fragList = model.getFragmentList();
  const fragbBox = new THREE.Box3();
  const nodebBox = new THREE.Box3();

  fragIds.forEach(function (fragId) {
    fragList.getWorldBounds(fragId, fragbBox);
    nodebBox.union(fragbBox);
  });

  return nodebBox;
}
export function getFragIdFromDbId(dbids, model = null) {
  if (!model) model = store.state.viewer.model;
  var returnValue = [];
  var it = model.getInstanceTree();
  for (const dbid of dbids) {
    it.enumNodeFragments(
      dbid,
      function (fragId) {
        returnValue.push(fragId);
      },
      false,
    );
  }
  return returnValue;
}
export function getBBBox(dbIds, model = null) {
  if (!model) model = store.state.viewer.model;

  if (!model) {
    return [];
  }

  let primitives = [];

  let tree = model.getInstanceTree();

  if (!tree) {
    Vue.prototype.$dialog.notify.warning('Model not fully loaded yet.');
    return [];
  }

  dbIds.forEach((dbId) => {
    tree.enumNodeChildren(
      dbId,
      (child) => {
        primitives.push(child);
      },
      true,
    );
  });

  let frags = getFragIdFromDbId(primitives);

  if (!frags.length)
    debugLog(`Failed to get the center for IDs:: ${dbIds.join(',')}`);

  return getModifiedWorldBoundingBox(frags, model);
}
/**
 * This function is used to get the world bounding box of the array's center primitive element of the dbIds primitive children.
 * We use this function mainly to get << a Tag Point >> on the center of one of its primitive children.
 * Previously we used the getBBox function BUT it returns the world bounding box for the children, which is often far away from the dbIds children.
 * @param {dbIds} dbIds
 * @param {model} model
 * @returns world bounding box.
 */
export function getMiddleBBoxForGroup(dbIds, model = null) {
  if (!model) model = store.state.viewer.model;

  var tree = model.getInstanceTree();

  let primitives = [];

  // Get primitive children for dbIds
  dbIds.forEach((dbId) => {
    tree.enumNodeChildren(
      dbId,
      (child) => {
        // Primitive child should not have children.
        if (!tree.getChildCount(child)) {
          primitives.push(child);
        }
      },
      true,
    );
  });

  // Get the center element.
  // TODO: we can get the center of each child boundary, then get the nearest one to the BBox function boundary center.
  if (primitives.length) primitives = [primitives[Math.round((primitives.length - 1) / 2)]];

  let frags = getFragIdFromDbId(primitives);

  if (!frags.length)
    debugLog(`Failed to get the center for IDs:: ${dbIds.join(',')}`);

  return getModifiedWorldBoundingBox(frags, model);
}
export function setPermeativeElementsTheme(dbIds, color) {
  let tree = store.state.viewer?.model?.getInstanceTree();

  if (!tree) return;

  for (let i = 0; i < dbIds.length; i++) {
    // https://aps.autodesk.com/blog/viewer-release-notes-v-65
    setThemingColor(store.state.viewer, dbIds[i], color, null);
  }
}

/**
 * Remove theming from selected elements without clearing the whole viewer.
 * Forge Model#setThemingColor accepts an undefined color to restore the
 * element's original material. Used when a narrowed Task target must settle
 * natural while its surrounding context remains ghost-gray.
 */
export function clearPermeativeElementsTheme(dbIds) {
  const viewer = store.state.viewer;
  const model = viewer?.model;
  if (!model?.getInstanceTree?.()) return;

  for (const dbId of dbIds || []) {
    model.setThemingColor(dbId, undefined, true);
    if (store.state.forgeService?.dbIdPerThemingColor) {
      delete store.state.forgeService.dbIdPerThemingColor[dbId];
    }
  }
  viewer.impl?.invalidate?.(true);
}
export function createButton(name, tooltip, classList, tooblar, isVisable, onClick) {
  var button1 = new Autodesk.Viewing.UI.Button(name);
  button1.onClick = function () {
    onClick();
  };
  for (var i = 0; i < classList.length; i++) {
    button1.addClass(classList[i]);
  }
  button1.setToolTip(tooltip);
  tooblar.addControl(button1);
  button1.setVisible(isVisable);

  return button1;
}
//https://gist.github.com/leefsmp/d38f1ce2b81ecf641c72d2b0011bc76c
export function pointerToRaycaster(viewer, pointer) {
  let domElement = viewer.canvas;
  let camera = viewer.impl.camera;
  const pointerVector = new THREE.Vector3();
  const pointerDir = new THREE.Vector3();
  const ray = new THREE.Raycaster();

  const rect = domElement.getBoundingClientRect();

  const x = ((pointer.clientX - rect.left) / rect.width) * 2 - 1;
  const y = -((pointer.clientY - rect.top) / rect.height) * 2 + 1;

  if (camera.isPerspective) {
    pointerVector.set(x, y, 0.5);

    pointerVector.unproject(camera);

    ray.set(camera.position, pointerVector.sub(camera.position).normalize());
  } else {
    pointerVector.set(x, y, -1);

    pointerVector.unproject(camera);

    pointerDir.set(0, 0, -1);

    ray.set(pointerVector, pointerDir.transformDirection(camera.matrixWorld));
  }

  return ray;
}
export async function getDbIdForgeObject(dbId, viewer = null) {
  if (!viewer) viewer = store.state.viewer

  // Persisted-write recipe: centers must be GLOBAL space (metres), never raw model.
  const { scale } = assertPersistableUnitScale(viewer?.model);

  return new Promise((resolve, reject) => {
    viewer.model.getObjectTree(function (instanceTree) {
      let promises = [];

      instanceTree.enumNodeChildren(
        dbId,
        function (dbId) {
          if (instanceTree.getChildCount(dbId) === 0) {
            let bBox = [];
            instanceTree.getNodeBox(dbId, bBox);
            let x = (bBox[0] + bBox[3]) / 2;
            let y = (bBox[1] + bBox[4]) / 2;
            let z = (bBox[2] + bBox[5]) / 2;

            let o = new ForgeObject();
            o.dbId = dbId;
            o.name = instanceTree.getNodeName(dbId);
            o.center = viewerToGlobal(new THREE.Vector3(x, y, z), scale);

            promises.push(Promise.resolve(o));
          }
        },
        true,
      );

      Promise.all(promises)
        .then((objects) => {
          resolve(objects);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}
export async function getDbIdBBox(dbId, viewer = null) {
  if (!viewer) viewer = this.viewer

  return new Promise((resolve) => {

    viewer.model.getObjectTree(function (instanceTree) {

      let bBox = [];

      instanceTree.getNodeBox(dbId, bBox);

      resolve(new THREE.Box3(new THREE.Vector3(bBox[0], bBox[1], bBox[2]), new THREE.Vector3(bBox[3], bBox[4], bBox[5])));
    });
  });
}
export function getDbIdCenter(dbId, viewer = null) {
  if (!viewer) viewer = this.viewer

  return new Promise((resolve) => {

    viewer.model.getObjectTree(function (instanceTree) {

      let bBox = [];

      instanceTree.getNodeBox(dbId, bBox);

      let x = (bBox[0] + bBox[3]) / 2;
      let y = (bBox[1] + bBox[4]) / 2;
      let z = (bBox[2] + bBox[5]) / 2;

      let center = viewerToGlobal(new THREE.Vector3(x, y, z));

      resolve(center);
    });
  });
}
export async function getLeafsObjectsAsync(viewer) {
  if (!viewer) return Promise.reject('Viewer is not defined.');

  // Same GLOBAL-space recipe as every other ForgeObject builder — these
  // elements are compared/mixed with persisted children centers.
  const { scale } = assertPersistableUnitScale(viewer?.model);

  console.time("Collecting leaf nodes");

  return new Promise((resolve, reject) => {
    viewer.model.getObjectTree(function (instanceTree) {
      let promises = [];

      instanceTree.enumNodeChildren(
        instanceTree.getRootId(),
        function (dbId) {
          if (instanceTree.getChildCount(dbId) === 0) {
            let bBox = [];
            instanceTree.getNodeBox(dbId, bBox);
            let x = (bBox[0] + bBox[3]) / 2;
            let y = (bBox[1] + bBox[4]) / 2;
            let z = (bBox[2] + bBox[5]) / 2;

            let o = new ForgeObject();
            o.dbId = dbId;
            o.name = instanceTree.getNodeName(dbId);
            o.center = viewerToGlobal(new THREE.Vector3(x, y, z), scale);

            promises.push(Promise.resolve(o));
          }
        },
        true,
      );

      Promise.all(promises)
        .then((objects) => {
          debugLog("=========================================")
          console.timeEnd("Collecting leaf nodes");
          debugLog("=========================================")
          resolve(objects);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}
export function getAllLeafComponents(model, callback) {
  model.getObjectTree(function (tree) {
    let promises = [];

    tree.enumNodeChildren(
      tree.getRootId(),
      function (dbId) {
        if (tree.getChildCount(dbId) === 0) {
          let promise = new Promise((resolve) => {
            resolve(dbId);
          });
          promises.push(promise);
        }
      },
      true,
    );

    Promise.all(promises)
      .then((dbIds) => {
        callback(dbIds);
      })
      .catch((error) => {
        debugError(error);
      });
  });
}
export function zoomToFit(viewer, dbIds, model, enableAnimation = true) {
  if (!viewer) return;

  let preferences = viewer.getSettingsPanel()?.preferences;

  if (!preferences) return;

  let currentMode = preferences.get('selectionMode');
  if (currentMode != Autodesk.Viewing.SelectionMode.LEAF_OBJECT) {
    setSelectionMode(viewer, Autodesk.Viewing.SelectionMode.LEAF_OBJECT);
  }

  viewer.select(dbIds, model);
  viewer.utilities.fitToView(!enableAnimation);
  viewer.clearSelection();

  if (currentMode != preferences.get('selectionMode')) {
    setSelectionMode(viewer, currentMode);
  }
}
export function setSelectionMode(viewer, mode) {
  if (!viewer) return;

  viewer
    .getSettingsPanel()
    ?.controls[
      'ViewerSettingsPanel0-0-adsk_settings_control_id_22'
    ]?.listeners?.change[0]?.callbackFn({
      detail: {
        value: mode,
      },
    });
}
export function takeScreenshotForDbids(viewer, dbids, callback, isolate = true) {
  var state = viewer.getState();
  viewer.setGhosting(false);
  zoomToFit(viewer, dbids, viewer.model, false);

  if (isolate) {
    viewer.clearSelection();
    viewer.isolate(dbids, viewer.model, false);
  } else {
    viewer.select(dbids);
  }

  store.state.forgeService.cameraZoomOutByScaler(0.25);

  setTimeout(() => {
    takeScreenshot(viewer, (base64) => {
      viewer.restoreState(state, null, true);
      if (callback) callback(base64);
    });
  }, 100);
}
export function takeScreenshotForDbidsNoRestore(viewer, dbids, callback) {
  if (!Array.isArray(dbids)) dbids = [dbids];

  viewer.setGhosting(false);

  let bbox = getBBBox(dbids, viewer.model);

  if (store.state.isLargeModel)
    bbox = bbox.expandByScalar(store.state.largeModelScalar);

  viewer.navigation.fitBounds(true, bbox);
  // zoomToFit(viewer, dbids, viewer.model, false);

  viewer.clearSelection();
  viewer.isolate(dbids, viewer.model, false);

  setTimeout(() => {
    takeScreenshot(viewer, (base64) => {
      if (callback) callback(base64);
    });
  }, 150);
}
export function take3dScaffoldScreenshot(scaffold, callback) {
  let viewer = store.state.viewer;

  var state = viewer.getState();

  let box = scaffold.getBox();
  viewer.navigation.fitBounds(true, box);

  setTimeout(() => {
    takeScreenshot(viewer, (base64) => {
      viewer.restoreState(state);
      if (callback) callback(base64);
    });
  }, 100);
}
// Element thumbnails for Set Position / Set Point. The settle time matches take3dPointScreenshot
// and takeScreenshotForDbids; the zoom-out matches takeScreenshotForDbids, so an element thumbnail
// is framed like the one taken when the tag was created.
export const ELEMENT_SCREENSHOT_SETTLE_MS = 100;
export const ELEMENT_SCREENSHOT_ZOOM_OUT = 0.25;

function hasFiniteBounds(box) {
  const { min, max } = box || {};
  if (!min || !max) return false;
  return [min.x, min.y, min.z, max.x, max.y, max.z].every(Number.isFinite)
    && max.x >= min.x && max.y >= min.y && max.z >= min.z;
}

/**
 * Screenshots model elements framed to their full bounds, then puts the camera back.
 *
 * Camera-only on purpose. `takeScreenshotForDbids` (unchanged, still used by tag creation) isolates
 * and selects, and in this app neither is undone by `restoreState` even though it re-applies them:
 * - every isolation change fires AGGREGATE_ISOLATION_CHANGED_EVENT; the listener in
 *   `mainExtension.js` clears the user's themed elements when isolation returns to empty and
 *   changes the near radius on large models;
 * - select / clearSelection fire SELECTION_CHANGED_EVENT, which rewrites the store selection and
 *   moves the model browser.
 * So this helper never isolates, selects or changes ghosting, and restores only the viewport - the
 * `{ viewport: true }` filter stops `restoreState` from re-applying the object set. The crop box is
 * hidden for the capture, like `take3dPointScreenshot`.
 *
 * @param {Object} viewer - Forge Viewer3D with a loaded model.
 * @param {number|number[]} dbIds - Element dbIds in `viewer.model`; ids that are not positive integers are ignored.
 * @param {function(string): void} callback - Receives the base64 data URL. Never called when this returns false.
 * @returns {boolean} True when a capture was scheduled; false when there was nothing to frame (the viewer was not touched).
 */
export function takeElementScreenshot(viewer, dbIds, callback) {
  const ids = (Array.isArray(dbIds) ? dbIds : [dbIds]).filter((id) => Number.isInteger(id) && id > 0);
  // getBBBox raises a "Model not fully loaded" toast when there is no instance tree - check first.
  if (!ids.length || !viewer?.model?.getInstanceTree?.()) return false;

  // An id with no fragments in this model (e.g. it belongs to another loaded model) has no bounds.
  const box = getBBBox(ids, viewer.model);
  if (!hasFiniteBounds(box)) return false;

  const viewport = viewer.getState({ viewport: true });
  const cropping = store.state.forgeService?.getCropping3DExtension?.();
  let restored = false;
  let cropHidden = false;
  const restore = () => {
    if (restored) return;
    restored = true;
    try {
      viewer.restoreState(viewport, { viewport: true }, true);
      // Only show the crop box again if this capture hid it.
      if (cropHidden) cropping?.updateBoxVisibility(true);
    } catch (err) {
      debugError('[ElementScreenshot] Failed to restore the camera', err);
    }
  };

  try {
    viewer.navigation.fitBounds(true, box);
    store.state.forgeService?.cameraZoomOutByScaler?.(ELEMENT_SCREENSHOT_ZOOM_OUT);
    cropping?.updateBoxVisibility(false);
    cropHidden = true;
  } catch (err) {
    restore();
    throw err;
  }

  setTimeout(() => {
    try {
      takeScreenshot(viewer, (base64) => {
        restore();
        if (callback) callback(base64);
      });
    } catch (err) {
      debugError('[ElementScreenshot] Capture failed', err);
      restore();
    }
  }, ELEMENT_SCREENSHOT_SETTLE_MS);

  return true;
}
export function takeScreenshot2DAroundPoint(viewer, point, callback) {
  var state = viewer.getState();
  var min = new THREE.Vector3(point.x - 0.01, point.y - 0.01, point.z - 0.01);
  var max = new THREE.Vector3(point.x + 0.01, point.y + 0.01, point.z + 0.01);
  var box = new THREE.Box3(min, max);
  viewer.navigation.fitBounds(true, box);
  setTimeout(() => {
    takeScreenshot(viewer, (base64) => {
      callback(base64);
      viewer.restoreState(state);
    });
  }, 100);
}
export function getForgeToken(callback) {
  HttpService.get({
    url: 'forge/oauth/token',
  }).then((response) => {
    callback(response.data.access_token, response.data.expires_in);
  });
}
export function setModelVisibility(viewer, model, isVisible) {
  if (!viewer.isLoadDone()) {
    viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, function () {
      setModelVisibility(viewer, model, isVisible);
    }, { once: true });
    return;
  }

  if (isVisible) {
    let state = viewer.getState();
    viewer.showModel(store.state.model3dId);
    let targetModel = viewer.getVisibleModels().find((m) => m.id == store.state.model3dId);
    if (targetModel) {
      viewer.impl.model = targetModel;
    }
    viewer.restoreState(state, null, true);
  } else {
    viewer.hideModel(store.state.model3dId);
  }

  viewer.impl.toggleGhosting(false);
  viewer.impl.sceneUpdated(true);
  viewer.impl.invalidate(true, true, true);
}
export function setModelTransparency(model, opacity) {
  let modelMaterials = [];
  let viewerMaterials = store.state.viewer.impl.getMaterials()._materials;
  for (let key of Object.keys(viewerMaterials)) {
    if (key == '__fadeMaterial__') continue;
    // if(key.startsWith(`model:${model.id}`))
    modelMaterials.push(viewerMaterials[key]);
  }
  modelMaterials.forEach((material) => {
    material.opacity = opacity;
    material.transparent = true;
    material.needsUpdate = true;
  });
  //model.unconsolidate();

  store.state.viewer.impl.invalidate(true, true, true);
}

//https://stackoverflow.com/questions/70152847/how-to-add-custom-material-to-a-fragment-in-forge-viewer
export function createMaterial(viewer, materialName, color, isTransparent) {
  var material = viewer.impl.getMaterials()._materials[materialName];
  if (material) return material;

  material = new THREE.MeshPhongMaterial({
    side: THREE.DoubleSide,
    reflectivity: 0,
    color: new THREE.Color(color),
    transparent: isTransparent,
  });
  const materials = viewer.impl.matman();
  materials.addMaterial(materialName, material, true);
  return material;
}

export function createGrayMaterial(viewer, materialName) {
  var material = viewer.impl.getMaterials()._materials[materialName];

  if (material) return material;

  let grayColor = new THREE.Color(0.75, 0.75, 0.75);

  material = new THREE.MeshPhongMaterial({
    side: THREE.DoubleSide,
    reflectivity: 0,
    shininess: 100,
    color: grayColor,
    emissive: new THREE.Color(0, 0, 0),
    transparent: true,
  });
  const materials = viewer.impl.matman();
  materials.addMaterial(materialName, material, true);
  return material;
}

export function applyMaterial(model, dbid, material) {
  const tree = model.getInstanceTree();
  const frags = model.getFragmentList();
  tree.enumNodeFragments(dbid, (fragid) => frags.setMaterial(fragid, material), true);
}
export function getAlldbIds(instanceTree) {
  var rootId = instanceTree.getRootId();
  var alldbId = [];
  if (!rootId) {
    return alldbId;
  }
  var queue = [];
  queue.push(rootId);
  while (queue.length > 0) {
    var node = queue.shift();
    alldbId.push(node);
    instanceTree.enumNodeChildren(node, function (childrenIds) {
      queue.push(childrenIds);
    });
  }
  return alldbId;
}

export function checkIsModelTrasnlated(urn, region, callback) {
  getForgeToken(function (access_token) {
    axios({
      baseURL: `https://developer.api.autodesk.com/modelderivative/v2/designdata/${urn}/manifest`,
      headers: {
          Authorization: 'Bearer ' + access_token,
          'x-ads-region': getRegionString(region)
        }
    })
      .then((response) => {
        if (response.data.status === 'success') {
          callback(true);
        } else {
          callback(response.data.progress);
        }
      })
      .catch((error) => {
        debugLog(error);
        callback(false);
      });
  });
}
export async function addViewable(viewer, urn, xform, offset) {
  return new Promise(function (resolve, reject) {
    function onDocumentLoadSuccess(doc) {
      const viewable = doc.getRoot().getDefaultGeometry();
      const options = {
        preserveView: true,
        keepCurrentModels: true,
        applyScaling: { to: store.getters['getDisplayUnit'] },
      };
      if (xform) {
        options.placementTransform = xform;
      }
      if (offset) {
        options.globalOffset = offset;
      }
      viewer.loadDocumentNode(doc, viewable, options).then(resolve).catch(reject);
    }
    function onDocumentLoadFailure(code) {
      reject(`Could not load document (${code}).`);
    }
    Autodesk.Viewing.Document.load('urn:' + urn, onDocumentLoadSuccess, onDocumentLoadFailure);
  });
}
export function isPointFrontOfCamera(point, forgeViewer) {
  var cameraPosition = forgeViewer.navigation.getPosition();
  var newDirection = forgeViewer.navigation.getCamera().getWorldDirection();
  var direc2D = toPoint2D(newDirection);
  var normalVec = new THREE.Vector2(-direc2D.y, direc2D.x);
  if (isLeft(point, cameraPosition, cameraPosition.clone().add(normalVec))) return true;
  return false;
}
export function getVisibleBoundingBox(model) {
  let fragList = model.getFragmentList();
  let fragsCount = fragList.fragments.fragId2dbId.length;
  let fragBox = new THREE.Box3();
  let nodeBox = new THREE.Box3();
  for (let i = 0; i < fragsCount; i++) {
    if (!model.isFragVisible(fragsCount[i])) continue;
    fragList.getWorldBounds(fragsCount[i], nodeBox);
    nodeBox.union(fragBox);
  }
  return nodeBox;
}
export function setForgeCropBox(box, viewer) {
  let transform = box && box.transform ? box.transform : null;

  const planes = !box ? null : Autodesk.Viewing.Private.SceneMath.box2CutPlanes(box, transform);
  viewer.impl.setCutPlaneSet('__set_view', planes);
}
export function removeFromScene(meshes, viewer, sceneName) {
  if (!meshes || !viewer || !viewer.impl) return;
  if (!(meshes instanceof Array)) meshes = [meshes];
  meshes.forEach((mesh) => {
    if (mesh.geometry) mesh.geometry.dispose();
    if (mesh.material) mesh.material.dispose();
    let scene = viewer.impl.overlayScenes[sceneName]?.scene;
    scene?.remove(mesh);
  });
}
export function addToScene(obj, viewer, sceneName) {
  if (!viewer || !viewer.overlays) return;
  if (!viewer.overlays.hasScene(sceneName)) viewer.overlays.addScene(sceneName);
  viewer.overlays.addMesh(obj, sceneName);
}
export function distanceToPointToRelativeDistance(point) {
  return store.state.viewer.navigation.getPosition().distanceTo(point) / 110;
}

/**
 * Calculate screen-space size based on camera distance and FOV
 * Similar to Potree's Utils.projectedRadius
 * @param {number} worldSize - Size in world units (typically 1)
 * @param {THREE.Camera} camera - The camera object
 * @param {number} distance - Distance from camera to point
 * @param {number} screenWidth - Screen width in pixels
 * @param {number} screenHeight - Screen height in pixels
 * @returns {number} Projected radius in world units
 */
export function projectedRadius(worldSize, camera, distance, screenWidth, screenHeight) {
  const fov = camera.fov * (Math.PI / 180); // Convert to radians
  const height = 2 * Math.tan(fov / 2) * distance;
  const width = height * (screenWidth / screenHeight);
  const pr = worldSize / width;
  return pr;
}

/**
 * Calculate screen-space size for a point using current viewer camera
 * @param {THREE.Vector3} point - World position
 * @param {number} desiredScreenSize - Desired size in screen pixels (default: 10)
 * @returns {number} World size to achieve desired screen size
 */
export function getScreenSpaceSize(point, desiredScreenSize = 10) {
  const viewer = store.state.viewer;
  if (!viewer || !viewer.impl || !viewer.impl.camera) {
    return 0.1; // Fallback
  }

  const camera = viewer.impl.camera;
  const distance = camera.position.distanceTo(point);
  const screenWidth = viewer.canvas.clientWidth || 1920;
  const screenHeight = viewer.canvas.clientHeight || 1080;

  const pr = projectedRadius(1, camera, distance, screenWidth, screenHeight);
  return desiredScreenSize / pr;
}
export function getURNThumbnail(urn, callback) {
  HttpService.get({
    url: `api/forge/getURNThmbnail?urn=${urn}`,
  })
    .then((response) => {
      callback(response.data);
    })
    .catch((error) => {
      debugLog('[forgeUtils] Error:', error);
    });
}

export function createTextGl_v1(text, size, color, center, viewer, sceneName = 'text-scene') {
    // 1. Device Pixel Ratio Setup
    const dpr = 1//window.dpr || window.devicePixelRatio || 1;

    // 2. DPR-adjusted Geometry
    const baseSize = size * dpr;
    const geom = new THREE.TextGeometry(text, {
      font: 'artifakt element',
      size: baseSize,
      height: 0,
      curveSegments: Math.max(3, Math.floor(3 * dpr)),
      bevelEnabled: false
    });

    const mat = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });


    const mesh = new THREE.Mesh(geom, mat);
    mesh.scale.set(dpr, dpr, 1); // Scale mesh instead of geometry

    // 3. Correct bounding box calculation
    geom.computeBoundingBox();
    const box = geom.boundingBox;
    const width = (box.max.x - box.min.x) * mesh.scale.x;
    const height = (box.max.y - box.min.y) * mesh.scale.y;

    mesh.position.set(
      center.x - (width / 2),
      center.y - (height / 2),
      center.z
    );

    addToScene(mesh, viewer, sceneName);
    return mesh;
}

//https://forge.autodesk.com/blog/how-do-you-add-labels-forge-viewer
export function createTextGl(text, size, color, center, viewer, sceneName) {
  let geom = new THREE.TextGeometry(text, {
    font: 'artifakt element',
    size: size,
    height: 0,
    curveSegments: 3,
  });

  //Here we compute it's boundingbox
  geom.computeBoundingBox();
  //Here we define the material for the geometry
  var mat = new THREE.MeshBasicMaterial({ color: color, depthTest: false, depthWrite: false });

  //Here we create the mesh from using the geometry and material
  let mesh = new THREE.Mesh(geom, mat);

  //Then, we set its position
  var width = geom.boundingBox.max.x - geom.boundingBox.min.x;
  var height = geom.boundingBox.max.y - geom.boundingBox.min.y;
  mesh.position.x = center.x - 0.5 * width;
  mesh.position.y = center.y - 0.5 * height;
  mesh.position.z = center.z;

  //Now we just need to add on a custom scene on viewer
  addToScene(mesh, viewer, sceneName);
  return mesh;
}
export function rotateViewerCamera(viewer, angle) {
  var nav = viewer.navigation;
  var cam = nav.getCamera();
  var upVector = cam?.up;
  var rotationMatrix = new THREE.Matrix4();
  rotationMatrix = rotationMatrix.makeRotationZ(angle);
  upVector.applyMatrix4(rotationMatrix);
  nav.setCameraUpVector(upVector, true);
}

export function rotateViewerMarkups(autodeskMarkupsExtension, angle) {
  var rotationMatrix = new THREE.Matrix4();
  rotationMatrix = rotationMatrix.makeRotationZ(-angle);
  autodeskMarkupsExtension.markups.forEach((m) => {
    var position = Object.assign(new THREE.Vector3(), m.position);
    position.applyMatrix4(rotationMatrix);
    m.setPosition(position.x, position.y);
    m.setRotation(m.getRotation() + angle);
  });
}

export function unloadViewerAllModels(viewer) {
  var models = viewer.getAllModels();
  if (viewer.model) {
    for (let i = 0; i < models.length; i++) {
      viewer.unloadModel(models[i]);
    }
  }
}

export function getViewerCurrentPdfPagesCount(viewer) {
  var pagesCount = viewer?.model?.getDocumentNode()?.getRootNode()?.children.length;
  return pagesCount;
}
export function getTagsFrom3DPoint(viewer, point3d, imagePosition, tolerance = 0.5, rayScale = 0.1) {
  if (!viewer || !point3d || !imagePosition) {
    debugError('Invalid input data for getting tag IDs');
    return [];
  }

  const elementPt = new THREE.Vector3(point3d.x, point3d.y, point3d.z);

  // Compute base point for raycasting
  const base = elementPt.clone().sub(imagePosition.clone().normalize().multiplyScalar(rayScale));
  const direction = elementPt.clone().sub(base);

  const ray = new THREE.Ray(base, direction);
  const intersections = [];

  const modelIntersection = viewer.impl?.rayIntersect(ray, false, null, null, intersections);

  if (!modelIntersection || modelIntersection.distance > tolerance) {
    // Define the cube's dimensions
    const halfSize = tolerance / 2;

    // Define offsets for 5 points on each cube face
    const offsets = [
      new THREE.Vector3(-halfSize, halfSize, 0), // Top left
      new THREE.Vector3(halfSize, halfSize, 0), // Top right
      new THREE.Vector3(-halfSize, -halfSize, 0), // Bottom left
      new THREE.Vector3(halfSize, -halfSize, 0), // Bottom right
      new THREE.Vector3(0, 0, 0), // Center
    ];

    // List of intersection results
    const cubeIntersections = [];

    for (const axis of ['x', 'y', 'z']) {
      for (const offset of offsets) {
        const faceCenter = elementPt.clone();
        faceCenter[axis] += halfSize; // Adjust for cube face
        const point = faceCenter.clone().add(offset);

        const cubeRay = new THREE.Ray(base, point.clone().sub(base).normalize());
        const faceIntersections = [];

        const cubeIntersection = viewer.impl?.rayIntersect(
          cubeRay,
          false,
          null,
          null,
          faceIntersections,
        );
        if (cubeIntersection && cubeIntersection.distance <= tolerance) {
          cubeIntersections.push(cubeIntersection);
        }
      }
    }

    // Sort intersections by distance
    const sortedIntersections = cubeIntersections.sort((a, b) => a.distance - b.distance);

    // Process all intersections in order until a valid tag is found
    for (const intersection of sortedIntersections) {
      const tagIds = getTagsFromIntersection(viewer, intersection);
      if (tagIds.length) {
        return tagIds; // Return as soon as valid tag IDs are found
      }
    }

    debugError('No valid tags found in sorted intersections');
    return [];
  } else {
    return getTagsFromIntersection(viewer, modelIntersection);
  }
}
export function getTagsFromIntersection(viewer, intersection) {
  const dbId = intersection.dbId;
  const dbIds = [dbId, ...getAllParentsIds(viewer, dbId)];

  // Fetch related tags
  const pipelines = store.getters.GET_ALL_EXISTING_PIPELINES_TAGS.filter((t) =>
    t.children.some((c) => dbIds.includes(c.dbId)),
  );

  const equipment = store.state.TAGS.allExistingAutomaticDbids.filter((t) =>
    t.children.some((c) => dbIds.includes(c.dbId)),
  );

  return [...pipelines, ...equipment];
}
export function getAllParentsIds(viewer, dbId) {
  let rootId = viewer?.model?.getDocumentNode()?.getRootNode()?.id ?? 0;
  let maxDepth = 50; // To prevent infinite recursion.
  let depth = 0;
  let currentId = dbId;
  let parents = [];

  while (currentId > rootId && depth < maxDepth) {
    depth++;
    currentId = getNodeParentId(viewer, currentId);
    parents.push(currentId);
  }

  return parents;
}
export function getNodeParentId(viewer, id) {
  return viewer?.model?.getInstanceTree()?.getNodeParentId(id);
}
export function getAllDbIds(viewer) {
  var instanceTree = viewer.model.getData().instanceTree;
  var allDbIdsStr = Object.keys(instanceTree.nodeAccess.dbIdToIndex);
  return allDbIdsStr.map(function (id) {
    return parseInt(id);
  });
}
export function processGetChunkProperties(viewer, chunk) {
  return new Promise((resolve, reject) => {
    let properties = [];
    let categories = {};
    try {
      viewer.model.getBulkProperties(
        chunk,
        null,
        function (result) {
          result.forEach((item) => {
            let modelProperties = item.properties;
            for (let i = 0; i < modelProperties.length; i++) {
              const { displayName, displayCategory } = modelProperties[i];

              if (!!displayCategory && displayCategory.startsWith('__')) continue;

              if (!categories[displayCategory]) categories[displayCategory] = [];

              if (!categories[displayCategory].includes(displayName))
                categories[displayCategory].push(displayName);

              if (!properties.includes(displayName)) properties.push(displayName);
            }
          });
          resolve({ categories, properties });
        },
        reject,
      );
    } catch (error) {
      reject(error);
    }
  });
}
export async function getAllModelProperties(viewer, callback) {
  debugLog('🔎 Loading all properties...');

  let values = await viewer.model.getPropertyHashes();

  let categories = {};
  let properties = [];

  for (let i = 0; i < values.length; i++) {
    let property = values[i];

    let displayName = property[1];

    let category = property[2];

    if (category.startsWith('__')) continue;

    if (!categories[category]) categories[category] = [];

    if (!categories[category].includes(displayName)) categories[category].push(displayName);

    if (!properties.includes(displayName)) properties.push(displayName);
  }

  properties.sort((a, b) => a.localeCompare(b));

  Object.entries(categories).forEach(([key, value]) => {
    categories[key] = value.sort((a, b) => a.localeCompare(b));
  });

  debugLog(`✅ Total properties loaded: ${properties.length}`);

  if (callback) callback({ properties, categories });

  return { properties, categories };
}
export function getAllModelProperties2(viewer, callback) {
  const instanceTree = viewer.model.getInstanceTree();
  let properties = [];
  let categories = {};
  let count = 0;
  try {
    instanceTree.enumNodeChildren(
      instanceTree.getRootId(),
      (id) => {
        viewer.getProperties(id, (forgeEle) => {
          for (let i = 0; i < forgeEle.properties.length; i++) {
            const { displayName, displayCategory } = forgeEle.properties[i];

            if (!!displayCategory && displayCategory.startsWith('__')) continue;

            if (!categories[displayCategory]) categories[displayCategory] = [];

            if (!categories[displayCategory].includes(displayName))
              categories[displayCategory].push(displayName);

            if (!properties.includes(displayName)) properties.push(displayName);
          }
        });
        count++;
        if (count >= 500000 || count >= instanceTree.nodeAccess.numNodes) {
          throw `💥 Too many nodes to get properties, aborting, count: ${instanceTree.nodeAccess.numNodes}`;
        }
      },
      true,
    );
  } catch (e) {
    debugLog(e);
  }
  setTimeout(() => {
    if (callback) callback({ properties, categories });
  }, 0);
}
export function getDbIdsProperties(viewer, dbIds) {
  return new Promise((resolve, reject) => {
    try {
      let properties = [];
      for (let i = 0; i < dbIds.length; i++) {
        const dbId = dbIds[i];

        viewer.getProperties(dbId, (result) => {
          properties.push(
            ...result.properties.filter(
              (t) => t.displayName !== 'child' && t.displayName !== 'parent',
            ),
          );
          if (i == dbIds.length - 1) resolve(properties);
        });
      }
    } catch (error) {
      reject(error);
    }
  });
}
export async function getByNameAsync(viewer, name) {
  if (!viewer) return new Promise.reject('Viewer is not defined.');
  if (!name) return new Promise.reject('Name is not defined.');

  return viewer.model.getPropertyDb().executeUserFunction(`function userFunction(pdb) {
    var dbIds = [];

    pdb.enumObjects(dbId => {
        let p = pdb.getObjectProperties(dbId);
        let pv = p && p.properties;
        if (!pv) return;

        if (p.name === '${name}') {
            dbIds.push(dbId);
        }
    });

    return dbIds;
  }`);
}
export async function getByPropertyAsync(model, property, value) {

  return new Promise((resolve, reject) => {
    if (!model) return reject('Model is not defined.');
    if (!property) return reject('Property is not defined.');
    if (!value) return reject('Value is not defined.');

    model.search(value, resolve, reject, [property], { includeInherited: true })
  });
}
export function getPropertyValues(viewer, property, maxLength, callback) {
  viewer.model.getBulkProperties([], [property], (elements) => {
    let res = [];
    for (let k = 0; k < elements.length; k++) {
      if (maxLength && res.length >= maxLength) break;
      if (viewer.model.isNodeExists(elements[k].dbId)) {
        let forgeEle = elements[k];
        var val = forgeEle.properties.find((o) => o.displayName == property);
        if (val && !res.includes(val.displayValue)) res.push(val.displayValue);
      }
    }
    callback(res);
  });
}
export function getObjectsMatchingProperty(
  dbids,
  viewer,
  property,
  values,
  isExactMatch,
  callback,
) {
  values = Array.isArray(values) ? values : [values];
  var lowerValues = values.map((value) => value.toLowerCase());
  viewer.model.getBulkProperties(dbids, [property], (elements) => {
    let res = [];
    for (let k = 0; k < elements.length; k++) {
      if (viewer.model.isNodeExists(elements[k].dbId)) {
        let forgeEle = elements[k];
        let canAdd = false;
        if (isExactMatch)
          canAdd = forgeEle.properties.some(
            (o) => o.displayName == property && values.includes(o.displayValue),
          );
        else
          canAdd = forgeEle.properties.some(
            (o) =>
              o.displayName == property &&
              !!o.displayValue &&
              lowerValues.some((x) => o.displayValue.toLowerCase().includes(x)),
          );
        if (canAdd) res.push(forgeEle);
      }
    }
    callback(res);
  });
}
export function getNodeSortedProperties(viewer, nodeId) {
  if (!viewer) return null;

  return new Promise((resolve, reject) => {
    viewer.getProperties(
      nodeId,
      (forgeEle) => {
        const forgeProperties = forgeEle.properties.sort((a, b) =>
          a.displayName.localeCompare(b.name, 'en-US', {
            numeric: 'true',
          }),
        );

        resolve(forgeProperties);
      },
      reject,
    );
  });
}
export function getNodePropertyByName(viewer, nodeId, property) {
  return new Promise((resolve, reject) => {
    viewer.getProperties(
      nodeId,
      (forgeEle) => {
        const forgeProperty = forgeEle.properties.find((t) => t.displayName === property);
        resolve(forgeProperty);
      },
      reject,
    );
  });
}
export function getNodeChildDbids(nodeId, instanceTree) {
  let res = [];
  instanceTree.enumNodeChildren(nodeId, (child) => {
    res.push(child);
  });
  return res;
}
export function getNodesAtDepth(rootId, instanceTree, depth) {
  const nodes = [];
  function traverse(nodeId, currentDepth) {
    if (currentDepth === depth) {
      nodes.push(nodeId);
    } else if (currentDepth < depth) {
      let children = getNodeChildDbids(nodeId, instanceTree);
      children.forEach((child) => traverse(child, currentDepth + 1));
    }
  }
  traverse(rootId, 0);
  return nodes;
}
export function getNodeChildDbidsForNode(parent, instanceTree) {
  var components = []; // store the results
  if (instanceTree && instanceTree.getChildCount(parent) != 0) {
    instanceTree.enumNodeChildren(
      parent,
      function (children) {
        var foundInChilds = getNodeChildDbidsForNode(children, instanceTree);
        components = components.concat(foundInChilds);
      },
      false,
    );
  } else {
    components.push(parent);
  }
  return components;
}

export function getNodeChildren(dbId, instanceTree) {
  let children = [];
  instanceTree.enumNodeChildren(dbId, (child) => {
    children.push(child);
  });

  return children;
}

/**
 * Apply Appearance options to Forge Model.
 * @augments forgeUtils.js
 *
 * @param {Autodesk.Viewing.Viewer3D}       viewer                      Viewer to apply appearacne into.
 * @param {Autodesk.Viewing.Model}          model                       Model to apply appearacne into.
 * @param {[string]}                        dbIds                       Array of elements dbId to apply appearance to.
 * @param {ForgeAppearanceOptions}          options                     Appearance options to be applied.
 * @param {string}                          options.colorHexOverride    Color [Hex] override to be applied to elements.
 * @param {bool}                            options.isZoomToFit         Zoom to fit selected elements
 * @param {bool}                            options.clear               Clears all previous viewer appearance overrides.
 * @param {ForgeVisiblityMode}              options.forgeVisiblityMode  VisibilityMode.
 * @param {Autodesk.Viewing.SelectionMode}  options.selectionMode       SelectionMode.
 * @param {Autodesk.Viewing.SelectionType}  options.selectionType       SelectionType.
 * @return {bool} Returns true if excuted successfully, otherwise returns false.
 */
export function applyForgeAppearance(viewer, model, dbIds, options) {
  model = model || viewer.model;
  options = options || new ForgeAppearanceOptions();
  options = Object.assign(new ForgeAppearanceOptions(), options);

  if (options.clear) {
    viewer.showAll();
    clearThemingColors(viewer);
    viewer.clearSelection();
  }

  viewer.setSelectionMode(options.selection);

  var colorHexOverride = options.colorHexOverride;
  if (colorHexOverride != null && colorHexOverride != '') {
    var colorVector4 = options.hexToVector4(colorHexOverride);
    dbIds.map((id) => setThemingColor(viewer, id, colorVector4, model));
  }

  viewer.fitToView(dbIds, model, false);
  switch (options.forgeVisiblityMode) {
    case ForgeVisiblityMode.ISOLATE:
      viewer.isolate(dbIds, model);
      return true;

    case ForgeVisiblityMode.SHOW:
      viewer.show(dbIds, model);
      return true;

    case ForgeVisiblityMode.HIDE:
      viewer.hide(dbIds, model);
      return true;

    case ForgeVisiblityMode.SELECT:
      viewer.select(dbIds, model, options.selectionType);
      return true;

    default:
      return false;
  }
}

export function isolateForge(viewer, model, dbIds, fitViewer = true) {
  if (!viewer || !dbIds) return;

  if (fitViewer) {
    if (dbIds.length < 1000) {
      zoomToFit(viewer, dbIds, model);
    } else {
      zoomToFit(viewer, [], model);
    }
  }

  store.state.forgeService.cameraZoomOutByScaler(0.25);

  viewer.isolate(dbIds, model);
}
export function clearThemingColors(viewer) {
  viewer.clearThemingColors(viewer.model);
  store.state.forgeService.dbIdPerThemingColor = {};
}
export function setThemingColor(viewer, dbid, color, model) {
  viewer.setThemingColor(dbid, color, model, true);
  store.state.forgeService.dbIdPerThemingColor[dbid] = color;
}

/**
 * Shared paint step for condition-level coloring: reset any prior theming,
 * isolate the in-scope element union, then theme each element by its color.
 * Used by InspectionFiltersDrawer (master/CML-level coloring) and the CML popup
 * action bar (show-in-3D ghost+highlight). Each caller builds dbIdColor +
 * inScopeDbIds and owns its own empty-scope messaging; this performs only the
 * viewer paint so the two callers can't drift apart. (AVA-437 S1)
 *
 * @param {object} viewer - Forge viewer instance (store.state.viewer)
 * @param {object} extension - the main extension exposing isolateDbIds (forgeService.getMainExtension())
 * @param {Map<number|string, { color: string, sev: number }>} dbIdColor - dbId → winning color
 * @param {Array<number|string>} inScopeDbIds - full isolation union
 */
// Neutral grey for an isolated element whose colouring value has no entry in the widget's
// value->hex map (e.g. a value that appeared after the map was authored): the element stays
// visible instead of vanishing. Shared so the grid and the chart cross-filter paint identically.
export const UNMAPPED_ISOLATE_COLOR = '#9E9E9E';

export function applyDbIdColorMap(viewer, extension, dbIdColor, inScopeDbIds) {
  const existingThemed = store.getters['GET_THEMED_DB_IDS'];
  if (existingThemed?.length && viewer) clearThemingColors(viewer);
  extension?.isolateDbIds([...new Set(inScopeDbIds)]);
  const byColor = new Map();
  dbIdColor.forEach((v, d) => {
    const arr = byColor.get(v.color) || [];
    arr.push(d);
    byColor.set(v.color, arr);
  });
  byColor.forEach((dbIds, hex) => setPermeativeElementsTheme(dbIds, $hexToVector4(hex)));
  store.commit('SET_THEMED_DB_IDS', [...dbIdColor.keys()]);
}

export function zoomToFitBox2D(viewer, box) {
  const size = box.getSize(new THREE.Vector3());
  if (size.x < 0.01 && size.y < 0.01 && size.z < 0.01) {
    let center = box.getCenter(new THREE.Vector3());
    box = new THREE.Box3().setFromCenterAndSize(center, new THREE.Vector3(0.01, 0.01, 0.01));
  }

  viewer.navigation.fitBounds(false, box);
}

export function scrollToElementInContainer(element, selector) {
  if (!element) return;
  let parentDiv = element.closest(selector);
  if (!parentDiv) return;
  const parentTop = parentDiv.offsetTop;
  const specificTop = element.offsetTop;
  const distance = specificTop - parentTop;
  parentDiv.scrollTop = distance;
}

export function dbIdsToForgeObjects(dbIds, model = null) {
  if (!model) model = store.state.viewer?.model;

  if (!model) {
    debugLog(`%c dbIdsToForgeObjects: model is not loaded`, 'color: red');
    return [];
  }

  var instanceTree = store.state.viewer?.model?.getData().instanceTree;
  if (!instanceTree) {
    debugLog(`%c dbIdsToForgeObjects: instanceTree is not loaded`, 'color: red');
    return [];
  }

  // One GLOBAL space for everything persisted on a ForgeObject — center AND
  // min/max (min/max were previously left in viewer space, a third space).
  const { scale } = assertPersistableUnitScale(model);

  var result = [];

  dbIds.forEach((dbId) => {
    var elem = new ForgeObject();

    elem.name = instanceTree.getNodeName(dbId);

    if (!elem.name) {
      debugLog(`%c dbIdsToForgeObjects: dbId ${dbId} has no name`, 'color: red');
      return;
    }

    elem.dbId = dbId;

    const bbox = dbId2box.computeBBox(model, dbId);

    if (bbox) {
      elem.min = viewerToGlobal(bbox.min, scale);
      elem.max = viewerToGlobal(bbox.max, scale);
    }

    elem.center = viewerToGlobal(getMiddleBBoxForGroup([dbId], model).getCenter(), scale);
    result.push(elem);
  });

  return result;
}

/**
 * Canonical dbId → ForgeObject builder for persisted writes. Emits the single
 * DB coordinate space (global metres) for the center. Throws
 * CoordinateSpaceError when no usable unit scale exists — callers that build
 * save payloads must not fall back to raw model coordinates silently.
 */
export function forgeObjectFromDbId(dbId, model = null, scale = null) {
  const m = model || store.state.viewer?.model;
  if (!m) return null;

  const instanceTree = m.getData().instanceTree;
  if (!instanceTree) return null;

  const effectiveScale = scale || assertPersistableUnitScale(m).scale;

  const elem = new ForgeObject();
  elem.dbId = dbId;
  elem.name = m.getInstanceTree().getNodeName(dbId);
  elem.center = viewerToGlobal(getMiddleBBoxForGroup([dbId], m).getCenter(), effectiveScale);

  return elem;
}

export function convertToMonthNameDate(d) {
  const date = new Date(d);

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();

  const formattedDate = `${day}-${months[monthIndex]}-${year}`;

  return formattedDate;
}

export function waitForValue(valueToCheck, maxTime) {
  return new Promise((resolve, reject) => {
    let timeElapsed = 0;
    const intervalId = setInterval(() => {
      if (valueToCheck()) {
        resolve();
        clearInterval(intervalId);
      } else {
        timeElapsed += 500;
        if (timeElapsed >= maxTime) {
          reject('Timeout');
          clearInterval(intervalId);
        }
      }
    }, 500);
  });
}
